/**
 * InvestigationDetail — one durable ADR-082 diagnostic investigation case.
 *
 * Renders the case's full re-hydration state (symptom+scope, hypotheses,
 * evidence had/needed, the append-only steps-tried timeline, current
 * intent/next action, budgets) and, when the case is `awaiting-approval`,
 * the cost-ranked acquisitions from its most recently proposed round as a
 * numbered list with an inline Approve control per option — mirroring
 * ImprovementPRReview.tsx's list-detail-plus-action shape (a Link back to
 * the list, a load() reusable for both mount and post-action refresh, and a
 * parseApiError helper that pulls the backend's {error,...} JSON body out of
 * the api client's "HTTP <status>: <body>" Error messages).
 *
 * RBAC reflects (never replaces) the backend's tiered approval gate
 * (ADR-077/ADR-082 Phase 4): `new_instrumentation`/`access_request` require
 * fleet_owner; every other kind accepts operator|fleet_owner. A plain
 * operator sees the fleet_owner-only options with their Approve control
 * disabled and a note explaining why — the backend still enforces this
 * itself (403 `insufficient_role_for_acquisition`) regardless of what the UI
 * shows, so a stale role in this tab can never grant an approval it
 * shouldn't.
 */
import { useCallback, useEffect, useState } from 'react'
import { Link, useParams } from 'react-router-dom'
import { api, ForbiddenError, UnauthorizedError } from '../api/client'
import type { Acquisition, DiagnosticInvestigation, InvestigationStep } from '../api/client'
import { useAuth } from '../context/AuthContext'
import { relativeTime } from '../utils/relativeTime'
import { statusBadgeClass } from '../utils/investigationStatus'
// ADR-148 D2 — shared with Investigations.tsx so the row and this page cannot disagree
// about what a case is waiting for.
import { waitingOn, operatorArtifacts } from '../utils/capabilityGap'

/** Extracts {status, code, message} from the "HTTP <status>: <json-body>" errors the api client throws. */
function parseApiError(err: unknown, fallback: string): { status: number | null; code: string; message: string } {
  if (!(err instanceof Error)) return { status: null, code: '', message: fallback }
  const m = /^HTTP (\d+): ([\s\S]*)$/.exec(err.message)
  if (!m) return { status: null, code: '', message: err.message || fallback }
  const status = Number(m[1])
  let message = fallback
  let code = ''
  try {
    const body = JSON.parse(m[2]) as { error?: string; message?: string; status?: string; required?: string }
    code = body.error || ''
    if (code === 'insufficient_role_for_acquisition') {
      message = `Requires ${body.required ?? 'fleet_owner'}.`
    } else if (code === 'not_awaiting_approval') {
      message = `This case is no longer awaiting approval (status: ${body.status ?? 'unknown'}).`
    } else if (code === 'acquisition_pr_budget_exhausted') {
      message = 'This case has already spent its acquisition-PR budget for this kind of change.'
    } else if (code === 'invalid_acquisition_index') {
      message = body.message || 'That acquisition option is no longer valid — refreshing.'
    } else {
      message = body.message || code || fallback
    }
  } catch {
    /* not JSON */
  }
  return { status, code, message }
}

/** Mirrors chat.AcquisitionRequiresFleetOwner (backend) — reflects, never replaces, the server gate. */
function acquisitionRequiresFleetOwner(kind: string): boolean {
  return kind === 'new_instrumentation' || kind === 'access_request'
}

/** Mirrors chat.LatestAcquisitions (backend): the acquisitions list from the MOST RECENT `acquisition_proposed` entry. */
function latestAcquisitions(steps: InvestigationStep[]): Acquisition[] {
  for (let i = steps.length - 1; i >= 0; i--) {
    if (steps[i].kind === 'acquisition_proposed') {
      return steps[i].acquisitions ?? []
    }
  }
  return []
}

function stepKindLabel(kind: string): string {
  const map: Record<string, string> = {
    acquisition_proposed: 'Acquisition proposed',
    acquisition_approved: 'Acquisition approved',
    resume_attempted: 'Resume attempted',
    resolved: 'Resolved',
    budget_exhausted: 'Budget exhausted',
    deadline_exceeded: 'Deadline exceeded',
  }
  return map[kind] ?? kind
}

function StepsTimeline({ steps }: { steps: InvestigationStep[] }) {
  if (steps.length === 0) {
    return <p className="text-sm text-gray-400">No steps recorded yet.</p>
  }
  // steps_tried is append-only server-side, so it already arrives chronological —
  // sort defensively anyway rather than trust ordering blind.
  const ordered = [...steps].sort((a, b) => Date.parse(a.ts) - Date.parse(b.ts))
  return (
    <ol className="space-y-3">
      {ordered.map((s, i) => (
        <li key={i} className="flex gap-3">
          <div className="w-2 h-2 rounded-full bg-gray-300 mt-1.5 shrink-0" aria-hidden="true" />
          <div className="min-w-0">
            <p className="text-sm text-gray-900">
              <span className="font-medium">{stepKindLabel(s.kind)}</span>
              <span className="text-xs text-gray-400 ml-2">{relativeTime(s.ts)}</span>
            </p>
            {s.detail && <p className="text-xs text-gray-600 mt-0.5 whitespace-pre-wrap break-words">{s.detail}</p>}
            {s.kind === 'acquisition_proposed' && (s.acquisitions ?? []).length > 0 && (
              <ol className="mt-1 text-xs text-gray-500 list-decimal list-inside space-y-0.5">
                {(s.acquisitions ?? []).map((a, j) => (
                  <li key={j}>[{a.kind}] {a.description} ({a.cost})</li>
                ))}
              </ol>
            )}
          </div>
        </li>
      ))}
    </ol>
  )
}

export function InvestigationDetail() {
  const { id } = useParams<{ id: string }>()
  const { user } = useAuth()
  const canAct = user.fleet_role === 'operator' || user.fleet_role === 'fleet_owner'

  const [inv, setInv] = useState<DiagnosticInvestigation | null>(null)
  const [loading, setLoading] = useState(true)
  const [loadError, setLoadError] = useState<string | null>(null)
  const [notFound, setNotFound] = useState(false)

  const [approving, setApproving] = useState<number | null>(null)
  const [actionError, setActionError] = useState<string | null>(null)
  const [actionNotice, setActionNotice] = useState<string | null>(null)

  const load = useCallback(async () => {
    if (!id) return
    setLoadError(null)
    setNotFound(false)
    try {
      const got = await api.getInvestigation(id)
      setInv(got)
    } catch (err) {
      if (err instanceof UnauthorizedError) throw err
      const { status, message } = parseApiError(err, 'Failed to load the investigation.')
      if (status === 404) setNotFound(true)
      else setLoadError(message)
    } finally {
      setLoading(false)
    }
  }, [id])

  useEffect(() => {
    setLoading(true)
    void load()
  }, [load])

  const handleApprove = useCallback(async (index: number) => {
    if (!id) return
    setApproving(index)
    setActionError(null)
    setActionNotice(null)
    try {
      const updated = await api.approveInvestigationAcquisition(id, index)
      setInv(updated)
      setActionNotice(`Approved option #${index} — the case is now awaiting evidence.`)
    } catch (err) {
      if (err instanceof UnauthorizedError) throw err
      if (err instanceof ForbiddenError) {
        setActionError('Permission denied — this acquisition requires fleet_owner.')
      } else {
        const { message } = parseApiError(err, 'Approve failed.')
        setActionError(message)
      }
      // The case may have moved out from under us (status/budget changed
      // concurrently) — refetch so the UI reflects live state either way.
      void load()
    } finally {
      setApproving(null)
    }
  }, [id, load])

  if (loading) {
    return <p className="text-sm text-gray-400 animate-pulse">Loading investigation...</p>
  }

  return (
    <div className="space-y-4">
      <Link to="/investigations" className="text-sm text-blue-600 hover:underline">← Back to Investigations</Link>

      {notFound && (
        <div className="rounded-lg border border-gray-200 bg-white px-4 py-8 text-center text-sm text-gray-400">
          Investigation not found.
        </div>
      )}

      {loadError && (
        <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">{loadError}</div>
      )}

      {inv && (
        <div className="space-y-4">
          {/* Header */}
          <div className="bg-white border border-gray-200 rounded-lg p-4 space-y-2">
            <div className="flex items-start justify-between gap-3">
              <p className="text-base font-semibold text-gray-900">{inv.symptom || '—'}</p>
              <span className={`text-xs px-2 py-0.5 rounded font-medium shrink-0 ${statusBadgeClass(inv.status)}`}>
                {inv.status}
              </span>
            </div>
            {inv.scope && Object.keys(inv.scope).length > 0 && (
              <p className="text-xs text-gray-500 font-mono">{JSON.stringify(inv.scope)}</p>
            )}
            <div className="flex items-center gap-4 text-xs text-gray-500 pt-1">
              <span>{inv.iterations}/{inv.max_iterations} iterations</span>
              <span>{inv.acquisition_prs}/{inv.max_acquisition_prs} acquisition PRs</span>
              {inv.deadline && <span>deadline {relativeTime(inv.deadline)}</span>}
              <span>updated {relativeTime(inv.updated_at)}</span>
            </div>
          </div>

          {/* Current intent / next action */}
          {(inv.current_intent || inv.next_action) && (
            <div className="bg-white border border-gray-200 rounded-lg p-4 space-y-2">
              {inv.current_intent && (
                <p className="text-sm text-gray-700"><span className="font-medium">Current intent:</span> {inv.current_intent}</p>
              )}
              {inv.next_action && (
                <p className="text-sm text-gray-700"><span className="font-medium">Next action:</span> {inv.next_action}</p>
              )}
            </div>
          )}

          {/* Terminal resolution */}
          {(inv.status === 'resolved' || inv.status === 'insufficient-evidence' || inv.status === 'abandoned') && inv.resolution && (
            <div className={`rounded-lg border px-4 py-3 text-sm ${
              inv.status === 'resolved' ? 'border-green-200 bg-green-50 text-green-800' : 'border-gray-200 bg-gray-50 text-gray-700'
            }`}>
              <p className="font-medium mb-0.5">{inv.status === 'resolved' ? 'Resolved' : 'Terminal — boundary reached'}</p>
              <p className="whitespace-pre-wrap break-words">{inv.resolution}</p>
            </div>
          )}

          {/* ADR-148 D2 — what this case needs from a HUMAN, for a capability gap.
              The envelope carried this all along (artifacts[] with an owner, and a
              proposed_shape naming the thing) and nothing rendered it, so a case whose
              only blocker was one allowlist entry looked indistinguishable from a case
              nobody could act on. Placed above the evidence grid because it is the only
              part of this page that asks the reader to do something. */}
          {inv.kind === 'capability_gap' && (() => {
            const mine = operatorArtifacts(inv)
            const waiting = waitingOn(inv)
            const shape = (inv.gap as { proposed_shape?: { kind?: string; detail?: string } } | null | undefined)?.proposed_shape
            if (mine.length === 0 && !waiting && !shape) return null
            return (
              <div className="bg-white border border-amber-200 rounded-lg p-4 space-y-2">
                <p className="text-sm font-medium text-gray-900">What this needs from you</p>
                {waiting && <p className="text-sm text-amber-800">{waiting}</p>}
                {mine.length > 0 ? (
                  <ul className="space-y-1.5">
                    {mine.map((a, i) => (
                      <li key={i} className="text-xs text-gray-700">
                        <span className="font-mono text-gray-900">{a.path_or_thing}</span>
                        {a.note && <span className="text-gray-500"> — {a.note}</span>}
                      </li>
                    ))}
                  </ul>
                ) : (
                  /* Honest rather than reassuring: an envelope with no operator-owned
                     artifact is waiting on core or the fleet, and saying "nothing for you"
                     is more useful than an empty list that reads as a rendering failure. */
                  <p className="text-xs text-gray-500">
                    No operator-owned change on this case — it is waiting on core or the fleet repo.
                  </p>
                )}
                {shape?.detail && (
                  <p className="text-xs text-gray-500 pt-1 border-t border-gray-100">
                    <span className="font-medium">proposed{shape.kind ? ` (${shape.kind})` : ''}:</span> {shape.detail}
                  </p>
                )}
              </div>
            )
          })()}

          {/* Evidence had / needed */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="bg-white border border-gray-200 rounded-lg p-4">
              <p className="text-sm font-medium text-gray-900 mb-2">Evidence had</p>
              {(inv.evidence_had ?? []).length === 0 ? (
                <p className="text-xs text-gray-400">None recorded.</p>
              ) : (
                <ul className="space-y-1.5 text-xs text-gray-600">
                  {(inv.evidence_had ?? []).map((e, i) => (
                    <li key={i}>
                      <span className="text-gray-800">{e.claim}</span>
                      {e.citation && <span className="text-gray-400"> — {e.citation}</span>}
                    </li>
                  ))}
                </ul>
              )}
            </div>
            <div className="bg-white border border-gray-200 rounded-lg p-4">
              <p className="text-sm font-medium text-gray-900 mb-2">Evidence needed</p>
              {(inv.evidence_needed ?? []).length === 0 ? (
                <p className="text-xs text-gray-400">None recorded.</p>
              ) : (
                <ul className="space-y-1.5 text-xs text-gray-600">
                  {(inv.evidence_needed ?? []).map((e, i) => (
                    <li key={i}>
                      <span className="text-gray-800">{e.observable}</span>
                      {e.why_unavailable && <span className="text-gray-400"> — {e.why_unavailable}</span>}
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>

          {actionError && (
            <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">{actionError}</div>
          )}
          {actionNotice && (
            <div className="rounded-lg border border-green-200 bg-green-50 px-4 py-3 text-sm text-green-800">{actionNotice}</div>
          )}

          {/* Inline acquisition approval */}
          {inv.status === 'awaiting-approval' && (
            <div className="bg-white border border-amber-200 rounded-lg p-4 space-y-3">
              <p className="text-sm font-medium text-gray-900">
                Ways to close the evidence gap (cheapest first)
              </p>
              {!canAct && (
                <p className="text-xs text-blue-700 bg-blue-50 border border-blue-200 rounded px-2 py-1">
                  View only — ask an operator or fleet owner to approve one.
                </p>
              )}
              {(() => {
                const acquisitions = latestAcquisitions(inv.steps_tried ?? [])
                if (acquisitions.length === 0) {
                  return <p className="text-xs text-gray-400">No acquisitions were proposed for this case.</p>
                }
                return (
                  <ol className="space-y-2">
                    {acquisitions.map((a, i) => {
                      const index = i + 1
                      const requiresOwner = acquisitionRequiresFleetOwner(a.kind)
                      const disabled = !canAct || (requiresOwner && user.fleet_role !== 'fleet_owner') || approving !== null
                      return (
                        <li key={i} className="flex items-start justify-between gap-3 border border-gray-100 rounded-md p-3">
                          <div className="min-w-0">
                            <p className="text-sm text-gray-900">
                              <span className="font-mono text-xs text-gray-400 mr-1.5">{index}.</span>
                              <span className="font-medium">[{a.kind}]</span> {a.description}
                            </p>
                            <p className="text-xs text-gray-500 mt-0.5">{a.rationale}</p>
                            <p className="text-xs text-gray-400 mt-0.5">cost: {a.cost}{requiresOwner ? ' · requires fleet_owner' : ''}</p>
                          </div>
                          <button
                            type="button"
                            onClick={() => { void handleApprove(index) }}
                            disabled={disabled}
                            title={requiresOwner && user.fleet_role !== 'fleet_owner' ? 'Requires fleet_owner' : ''}
                            className="shrink-0 px-3 py-1.5 rounded bg-indigo-600 text-white text-xs font-medium hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                          >
                            {approving === index ? 'Approving…' : 'Approve'}
                          </button>
                        </li>
                      )
                    })}
                  </ol>
                )
              })()}
            </div>
          )}

          {/* Steps-tried timeline */}
          <div className="bg-white border border-gray-200 rounded-lg p-4">
            <p className="text-sm font-medium text-gray-900 mb-3">Steps tried</p>
            <StepsTimeline steps={inv.steps_tried ?? []} />
          </div>
        </div>
      )}
    </div>
  )
}
